import React, {
    useEffect,
    useState,
    useMemo,
    useCallback,
} from "react";
import { Form } from "react-router-dom";
import { toast } from "react-toastify";
import { gql, useMutation } from "@apollo/client";

import { GlobalStyles } from "../../constants/styles";
import FilePicker from "../common/FilePicker";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import {getLocalizedGraphQLError} from "../../util/graphqlError";

const UPDATE_ORGANIZATION_BRANDING = gql`
    mutation UpdateOrganizationBranding($input: UpdateOrganizationBrandingInput!, $logo: Upload) {
        updateOrganizationBranding(input: $input, logo: $logo) {
            success
            errors
            branding {
                id
                subdomain
                primaryColor
                secondaryColor
                accentColor
                backgroundColor
                surfaceColor
                borderColor
                textOnBackground
                textOnSurface
                textOnPrimary
                textOnSecondary
                logoUrl
            }
            organization {
                id
            }
        }
    }
`;

function normalizeHex(value) {
    if (!value) return value;
    let v = value.trim().toUpperCase();
    if (!v.startsWith("#")) v = `#${v}`;

    if (/^#[0-9A-F]{3}$/i.test(v)) {
        v = `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
    }

    return v;
}

function handleHexPaste(e, setter) {
    const pasted = e.clipboardData.getData("text");
    const normalized = normalizeHex(pasted);

    if (/^#[0-9A-F]{6}$/i.test(normalized)) {
        e.preventDefault();
        setter(normalized);
    }
}

export default function EditOrgBrandingModal({
                                                 show,
                                                 organization,
                                                 onClose,
                                                 onUpdated
                                             }) {
    const [updateBranding] = useMutation(UPDATE_ORGANIZATION_BRANDING);
    const { t } = useTranslation();

    const scopeId = useMemo(
        () => `kbola-edit-org-branding-${Math.random().toString(36).slice(2, 9)}`,
        []
    );

    const API_BASE = useMemo(() => process.env.REACT_APP_API_HOST, []);

    const themeColors = useThemeColors();

    const normalizeUrl = useCallback(
        (u) => {
            if (!u) return null;
            if (/^(blob:|data:)/i.test(u)) return u;
            if (/^https?:\/\//i.test(u)) return u;

            const base = String(API_BASE || "").replace(/\/$/, "");
            if (!base) return u;

            return u.startsWith("/") ? `${base}${u}` : `${base}/${u}`;
        },
        [API_BASE]
    );

    const [subdomain, setSubdomain] = useState("");
    const [primaryColor, setPrimaryColor] = useState("#00adff");
    const [secondaryColor, setSecondaryColor] = useState("#006CA8");
    const [accentColor, setAccentColor] = useState("#8d929d");

    const [backgroundColor, setBackgroundColor] = useState("#14151a");
    const [surfaceColor, setSurfaceColor] = useState("#1f1f2e");
    const [borderColor, setBorderColor] = useState("#2a2a3a");

    const [textOnBackground, setTextOnBackground] = useState("#ffffff");
    const [textOnSurface, setTextOnSurface] = useState("#ffffff");
    const [textOnPrimary, setTextOnPrimary] = useState("#14151a");
    const [textOnSecondary, setTextOnSecondary] = useState("#ffffff");

    const [isActive, setIsActive] = useState(true);

    const [logoFile, setLogoFile] = useState(null);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

    const [uploading, setUploading] = useState(false);

    const modalStyles = useMemo(() => {
        const primary = themeColors.primary;

        return {
            wrap: {
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(primary, 0.50)},
                    ${alphaFromHex(primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: { display: "flex", alignItems: "center", gap: 10 },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.04em",
                padding: "6px 10px",
                borderRadius: 999,
                color: themeColors.textOnPrimary,
                background: themeColors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
            },
            title: {
                margin: 0,
                fontSize: 16,
                fontWeight: 900,
                color: themeColors.textOnPrimary,
            },
            closeBtn: {
                width: 36,
                height: 36,
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.10)",
                background: themeColors.primary,
                color: themeColors.textOnPrimary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            body: {
                padding: 14,
                background: "#fff",
            },
            sectionTitle: {
                margin: "2px 0 10px",
                fontSize: 13,
                fontWeight: 900,
                color: GlobalStyles.colors.woodsmoke,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
            },
            card: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                marginBottom: 12,
            },
            label: {
                fontSize: 12.5,
                fontWeight: 800,
                color: "rgba(20,21,26,0.70)",
                marginBottom: 6,
            },
            input: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "10px 12px",
                fontWeight: 700,
                outline: "none",
            },
            colorInput: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: 4,
                width: "100%",
                height: 44,
                cursor: "pointer",
                background: "#fff",
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.3)",
            },
            hint: {
                marginTop: 6,
                color: "rgba(20,21,26,0.55)",
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.25,
            },
            checkboxRow: {
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 6,
                fontSize: 13,
                fontWeight: 700,
                color: "rgba(20,21,26,0.70)",
            },
            previewRow: {
                display: "flex",
                gap: 8,
                marginTop: 10,
            },
            previewSwatch: {
                flex: 1,
                borderRadius: 12,
                padding: 10,
                border: "1px solid rgba(0,0,0,0.12)",
                display: "flex",
                flexDirection: "column",
                gap: 4,
            },
            previewBadge: {
                fontSize: 11,
                fontWeight: 800,
                opacity: 0.8,
            },
            footer: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 14px",
                background: "rgba(0,0,0,0.02)",
                borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
            },
            btnGhost: {
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.12)",
                color: "#14151a",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
            btnReset: {
                background: "#fffbea",
                border: "1px solid rgba(240, 200, 0, 0.35)",
                color: "#6c4a00",
            },
        };
    }, [themeColors]);

    useEffect(() => {
        if (!organization) return;

        const b = organization.branding || {};

        setSubdomain(b.subdomain || organization.slug || "");

        setPrimaryColor(b.primaryColor || "#00adff");
        setSecondaryColor(b.secondaryColor || "#006CA8");
        setAccentColor(b.accentColor || "#8d929d");

        setBackgroundColor(b.backgroundColor || "#14151a");
        setSurfaceColor(b.surfaceColor || "#1f1f2e");
        setBorderColor(b.borderColor || "#2a2a3a");

        setTextOnBackground(b.textOnBackground || "#ffffff");
        setTextOnSurface(b.textOnSurface || "#ffffff");
        setTextOnPrimary(b.textOnPrimary || "#14151a");
        setTextOnSecondary(b.textOnSecondary || "#ffffff");

        setIsActive(typeof b.isActive === "boolean" ? b.isActive : true);

        setLogoFile(null);
        setLogoPreviewUrl(normalizeUrl(b.logoUrl || null));
    }, [organization, normalizeUrl]);

    useEffect(() => {
        if (!logoFile) return;
        const url = URL.createObjectURL(logoFile);
        setLogoPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [logoFile]);

    const clearLogo = () => {
        setLogoFile(null);
        const b = organization?.branding || {};
        setLogoPreviewUrl(normalizeUrl(b.logoUrl || null));
    };

    const resetToDefaultPalette = () => {
        setPrimaryColor("#00adff");
        setSecondaryColor("#006CA8");
        setAccentColor("#8d929d");

        setBackgroundColor("#14151a");
        setSurfaceColor("#1f1f2e");
        setBorderColor("#2a2a3a");

        setTextOnBackground("#ffffff");
        setTextOnSurface("#ffffff");
        setTextOnPrimary("#14151a");
        setTextOnSecondary("#ffffff");
    };

    if (!show || !organization) return null;

    async function handleSubmit(e) {
        e.preventDefault();
        setUploading(true);

        try {
            const variables = {
                input: {
                    organizationId: organization.id,
                    subdomain: subdomain?.trim() || organization.slug,
                    primaryColor,
                    secondaryColor,
                    accentColor,
                    backgroundColor,
                    surfaceColor,
                    borderColor,
                    textOnBackground,
                    textOnSurface,
                    textOnPrimary,
                    textOnSecondary,
                    isActive,
                },
                logo: logoFile || null,
            };

            const { data } = await updateBranding({ variables });

            const payload = data?.updateOrganizationBranding;
            if (!payload?.success) {
                toast.error(t("editOrganizationBranding.updateFailed"), {
                    autoClose: 3000,
                    toastId: "org_branding_failed",
                    position: "top-center",
                    closeOnClick: true,
                });
                return;
            }

            const updatedOrg = payload.organization || organization;

            if (typeof onUpdated === "function") {
                onUpdated(updatedOrg, payload.branding);
            }

            toast.success(t("editOrganizationBranding.updatedSuccess"), {
                autoClose: 3000,
                toastId: "org_branding_edited",
                position: "top-center",
                closeOnClick: true,
            });

            onClose();
            setTimeout(() => window.location.reload(), 3000);
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "editOrganizationBranding.updateFailed"), {
                autoClose: 3500,
                toastId: "org_branding_error",
                position: "top-center",
            });
        } finally {
            setUploading(false);
        }
    }

    return (
        <>
            <div
                className="modal fade show d-block"
                id="edit-org-branding-modal"
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
            >
                <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
                    <div className="modal-content" style={modalStyles.wrap}>
                        <Form onSubmit={handleSubmit}>
                            <div className="modal-header" style={modalStyles.header}>
                                <div style={modalStyles.titleWrap}>
                                    <span style={modalStyles.badge}>
                                        {t("editOrganizationBranding.badge")}
                                    </span>
                                    <h4 className="modal-title" style={modalStyles.title}>
                                        {t("editOrganizationBranding.title", { name: organization.name })}
                                    </h4>
                                </div>

                                <button
                                    type="button"
                                    onClick={onClose}
                                    aria-label={t("editOrganizationBranding.close")}
                                    style={modalStyles.closeBtn}
                                    title={t("editOrganizationBranding.close")}
                                    disabled={uploading}
                                >
                                    <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1 }}>
                                        &times;
                                    </span>
                                </button>
                            </div>

                            <div className="modal-body" style={modalStyles.body}>
                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>
                                        {t("editOrganizationBranding.identity")}
                                    </div>

                                    <FilePicker
                                        scopeId={scopeId}
                                        colors={themeColors}
                                        label={t("editOrganizationBranding.logoLabel")}
                                        hint={t("editOrganizationBranding.logoHint")}
                                        accept="image/*"
                                        value={logoFile}
                                        previewUrl={logoPreviewUrl}
                                        onChangeFile={setLogoFile}
                                        onClear={clearLogo}
                                    />

                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                        <label style={modalStyles.label}>
                                            {t("editOrganizationBranding.subdomain")}
                                        </label>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                required
                                                value={subdomain}
                                                onChange={(e) =>
                                                    setSubdomain(
                                                        e.target.value
                                                            .replace(/[^a-z0-9-]/gi, "")
                                                            .toLowerCase()
                                                    )
                                                }
                                                placeholder={t("editOrganizationBranding.subdomainPlaceholder")}
                                                style={modalStyles.input}
                                            />
                                        </div>
                                        <div style={modalStyles.hint}>
                                            {t("editOrganizationBranding.subdomainHint", {
                                                subdomain: subdomain || "your-league",
                                            })}
                                        </div>
                                        <div style={modalStyles.checkboxRow}>
                                            <input
                                                id={`${scopeId}-is-active`}
                                                type="checkbox"
                                                checked={isActive}
                                                onChange={(e) => setIsActive(e.target.checked)}
                                            />
                                            <label
                                                htmlFor={`${scopeId}-is-active`}
                                                style={{ margin: 0, cursor: "pointer" }}
                                            >
                                                {t("editOrganizationBranding.enablePortal")}
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>
                                        {t("editOrganizationBranding.brandColors")}
                                    </div>

                                    <div className="row">
                                        <div className="col-md-4 mb-2">
                                            <label style={modalStyles.label}>
                                                {t("editOrganizationBranding.primary")}
                                            </label>
                                            <input
                                                type="color"
                                                className="form-control"
                                                value={primaryColor}
                                                onChange={(e) => setPrimaryColor(e.target.value)}
                                                onPaste={(e) => handleHexPaste(e, setPrimaryColor)}
                                                style={modalStyles.colorInput}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-2">
                                            <label style={modalStyles.label}>
                                                {t("editOrganizationBranding.secondary")}
                                            </label>
                                            <input
                                                type="color"
                                                className="form-control"
                                                value={secondaryColor}
                                                onChange={(e) => setSecondaryColor(e.target.value)}
                                                onPaste={(e) => handleHexPaste(e, setSecondaryColor)}
                                                style={modalStyles.colorInput}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-2">
                                            <label style={modalStyles.label}>
                                                {t("editOrganizationBranding.accent")}
                                            </label>
                                            <input
                                                type="color"
                                                className="form-control"
                                                value={accentColor}
                                                onChange={(e) => setAccentColor(e.target.value)}
                                                onPaste={(e) => handleHexPaste(e, setAccentColor)}
                                                style={modalStyles.colorInput}
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn mt-2"
                                        style={{ ...modalStyles.btn, ...modalStyles.btnReset }}
                                        onClick={resetToDefaultPalette}
                                        disabled={uploading}
                                    >
                                        <i className="fas fa-undo" />
                                        {t("editOrganizationBranding.resetDefaults")}
                                    </button>

                                    <div style={modalStyles.hint}>
                                        {t("editOrganizationBranding.brandColorsHint")}
                                    </div>
                                </div>

                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>
                                        {t("editOrganizationBranding.backgroundSurface")}
                                    </div>

                                    <div className="row">
                                        <div className="col-md-4 mb-2">
                                            <label style={modalStyles.label}>
                                                {t("editOrganizationBranding.background")}
                                            </label>
                                            <input
                                                type="color"
                                                className="form-control"
                                                value={backgroundColor}
                                                onChange={(e) => setBackgroundColor(e.target.value)}
                                                onPaste={(e) => handleHexPaste(e, setBackgroundColor)}
                                                style={modalStyles.colorInput}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-2">
                                            <label style={modalStyles.label}>
                                                {t("editOrganizationBranding.surface")}
                                            </label>
                                            <input
                                                type="color"
                                                className="form-control"
                                                value={surfaceColor}
                                                onChange={(e) => setSurfaceColor(e.target.value)}
                                                onPaste={(e) => handleHexPaste(e, setSurfaceColor)}
                                                style={modalStyles.colorInput}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-2">
                                            <label style={modalStyles.label}>
                                                {t("editOrganizationBranding.border")}
                                            </label>
                                            <input
                                                type="color"
                                                className="form-control"
                                                value={borderColor || "#2a2a3a"}
                                                onChange={(e) => setBorderColor(e.target.value)}
                                                onPaste={(e) => handleHexPaste(e, setBorderColor)}
                                                style={modalStyles.colorInput}
                                            />
                                        </div>
                                    </div>

                                    <div style={modalStyles.hint}>
                                        {t("editOrganizationBranding.backgroundSurfaceHint")}
                                    </div>
                                </div>

                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>
                                        {t("editOrganizationBranding.textColors")}
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-2">
                                            <label style={modalStyles.label}>
                                                {t("editOrganizationBranding.textOnBackground")}
                                            </label>
                                            <input
                                                type="color"
                                                className="form-control"
                                                value={textOnBackground}
                                                onChange={(e) => setTextOnBackground(e.target.value)}
                                                onPaste={(e) => handleHexPaste(e, setTextOnBackground)}
                                                style={modalStyles.colorInput}
                                            />
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <label style={modalStyles.label}>
                                                {t("editOrganizationBranding.textOnSurface")}
                                            </label>
                                            <input
                                                type="color"
                                                className="form-control"
                                                value={textOnSurface}
                                                onChange={(e) => setTextOnSurface(e.target.value)}
                                                onPaste={(e) => handleHexPaste(e, setTextOnSurface)}
                                                style={modalStyles.colorInput}
                                            />
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-2">
                                            <label style={modalStyles.label}>
                                                {t("editOrganizationBranding.textOnPrimary")}
                                            </label>
                                            <input
                                                type="color"
                                                className="form-control"
                                                value={textOnPrimary}
                                                onChange={(e) => setTextOnPrimary(e.target.value)}
                                                onPaste={(e) => handleHexPaste(e, setTextOnPrimary)}
                                                style={modalStyles.colorInput}
                                            />
                                        </div>
                                        <div className="col-md-6 mb-0">
                                            <label style={modalStyles.label}>
                                                {t("editOrganizationBranding.textOnSecondary")}
                                            </label>
                                            <input
                                                type="color"
                                                className="form-control"
                                                value={textOnSecondary}
                                                onChange={(e) => setTextOnSecondary(e.target.value)}
                                                onPaste={(e) => handleHexPaste(e, setTextOnSecondary)}
                                                style={modalStyles.colorInput}
                                            />
                                        </div>
                                    </div>

                                    <div style={modalStyles.previewRow}>
                                        <div
                                            style={{
                                                ...modalStyles.previewSwatch,
                                                background: backgroundColor,
                                                color: textOnBackground,
                                            }}
                                        >
                                            <span style={modalStyles.previewBadge}>
                                                {t("editOrganizationBranding.backgroundPreview")}
                                            </span>
                                            <span style={{ fontWeight: 800, fontSize: 13 }}>
                                                {t("editOrganizationBranding.backgroundPreviewTitle")}
                                            </span>
                                            <span style={{ fontSize: 12 }}>
                                                {t("editOrganizationBranding.backgroundPreviewText")}
                                            </span>
                                        </div>

                                        <div
                                            style={{
                                                ...modalStyles.previewSwatch,
                                                background: primaryColor,
                                                color: textOnPrimary,
                                            }}
                                        >
                                            <span style={modalStyles.previewBadge}>
                                                {t("editOrganizationBranding.primaryPreview")}
                                            </span>
                                            <span style={{ fontWeight: 800, fontSize: 13 }}>
                                                {t("editOrganizationBranding.primaryPreviewTitle")}
                                            </span>
                                            <span style={{ fontSize: 12 }}>
                                                {t("editOrganizationBranding.primaryPreviewText")}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer" style={modalStyles.footer}>
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={onClose}
                                    style={{ ...modalStyles.btn, ...modalStyles.btnGhost }}
                                    disabled={uploading}
                                >
                                    <i className="fas fa-times" />
                                    {t("editOrganizationBranding.close")}
                                </button>

                                <div>
                                    <button
                                        type="submit"
                                        className="btn"
                                        style={{ ...modalStyles.btn, ...modalStyles.btnPrimary }}
                                        disabled={uploading}
                                        title={
                                            uploading
                                                ? t("editOrganizationBranding.saving")
                                                : t("editOrganizationBranding.saveBranding")
                                        }
                                    >
                                        <i className="fas fa-check" />
                                        {uploading
                                            ? t("editOrganizationBranding.saving")
                                            : t("editOrganizationBranding.save")}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>

            <style>{`
                #edit-org-branding-modal.modal {
                  background: rgba(0, 0, 0, 0.45);
                  backdrop-filter: blur(2px);
                }

                #edit-org-branding-modal .modal-dialog {
                  max-height: 92vh;
                  display: flex;
                  flex-direction: column;
                  margin-top: 1.25rem;
                  margin-bottom: 1.25rem;
                }

                #edit-org-branding-modal .modal-content {
                  flex: 1;
                  overflow: hidden;
                  display: flex;
                  flex-direction: column;
                }

                #edit-org-branding-modal .modal-body {
                  overflow-y: auto;
                  max-height: 66vh;
                }

                #edit-org-branding-modal .form-control:focus {
                  border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.9);
                  box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                }

                @media (max-width: 768px) {
                  #edit-org-branding-modal .modal-dialog {
                    max-width: 95%;
                    margin: 0.9rem auto;
                  }
                  #edit-org-branding-modal .modal-body {
                    max-height: 70vh;
                  }
                }
            `}</style>
        </>
    );
}
